import {
  Id,
  Member,
  MembersData,
  MembersResponse as Response,
  Server,
  convertMembers,
} from '@nextforce/api'
import { useQuery } from '@tanstack/react-query'
import { t } from 'app/i18l'
import { useServer } from 'app/server/context'
import url from 'url'
import { useUser } from 'users/queries/detail'
import keys from '../../shared/queries/keys'

export function useMembers(
  conversationId: Id,
  enabled = true,
  memberIds?: Id[],
  alias = false
) {
  const { server } = useServer()
  const { data: user } = useUser()
  const isMembersQueryEnabled = enabled && !!conversationId
  const membersQueryKey = alias
    ? keys.membersWithAlias(conversationId)
    : keys.members(conversationId, memberIds ?? [])
  const query = useQuery<MembersData>({
    queryKey: membersQueryKey,
    queryFn: () =>
      fetchMembers({
        memberIds,
        conversationId,
        server,
        alias,
      }),
    enabled: isMembersQueryEnabled,
  })

  if (!user || !query.data || memberIds) {
    return query
  }

  const userMember: Member = {
    id: user.id,
    firstName: t('you'),
    lastName: '',
    photoUrl: user.photoUrl,
    roles: [user?.role ?? 'helper'],
    hasApp: user.hasApp,
    phoneNumber: user.phoneNumber,
  }

  const userIndex = query.data.findIndex(member => member.id === user.id)
  if (userIndex !== -1) {
    return {
      ...query,
      data: [
        userMember,
        ...query.data.slice(0, userIndex),
        ...query.data.slice(userIndex + 1),
      ],
    }
  }

  return {
    ...query,
    data: [userMember, ...query.data],
  }
}

export async function fetchMembers({
  memberIds,
  conversationId,
  server,
  fetchStaffAndGuestUsers = false,
  alias = false,
}: {
  memberIds?: string[]
  conversationId: string
  server: Server
  fetchStaffAndGuestUsers?: boolean
  alias?: boolean
}) {
  const queryParams: { [key: string]: string | boolean | number } = {}
  const baseUrl = `/v2/chat/conversations/${conversationId}/members`

  if (memberIds) {
    queryParams.member_ids = memberIds.join(',')
  }
  if (fetchStaffAndGuestUsers) {
    queryParams.add_staff_and_guest_users = true
  }
  if (alias) {
    queryParams.alias = true
  }

  const finalUrl = url.format({
    pathname: baseUrl,
    query: queryParams,
  })

  const response = await server.get<Response.Member[]>(finalUrl)

  return convertMembers(response) as MembersData
}

export function useMembersWithAlias(conversationId: Id, enabled = true) {
  return useMembers(conversationId, enabled, undefined, true)
}
